/**
 * XML → CSV Tool
 *
 * Converts XML to CSV by parsing to a JS object, flattening repeating elements,
 * and serializing with PapaParse.
 */

import { XMLParser } from 'fast-xml-parser';
import * as Papa from 'papaparse';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

export const XmlToCsvTool: ITransformerTool = {
  id: 'xml-to-csv',
  name: 'XML → CSV',
  description: 'Convert XML to CSV',
  version: '1.0.0',
  type: 'transformer',
  category: 'Converters',
  keywords: ['xml', 'csv', 'convert', 'transform', 'flatten'],

  seo: {
    slug: 'xml-to-csv',
    pageTitle: 'XML to CSV Converter - Free Online Tool | SWE DevTools',
    metaDescription: 'Convert XML data to CSV format instantly. Automatically flattens nested elements into tabular rows. Fast, private, runs entirely in your browser.',
    keywords: ['xml to csv', 'xml to csv converter', 'convert xml to csv online', 'xml csv transformer'],
    relatedTools: ['csv-to-xml', 'xml-to-json', 'json-to-csv'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.XML, SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.CSV,

  attributes: [
    {
      name: 'includeHeader',
      type: 'boolean',
      label: 'Include Header',
      defaultValue: true,
      description: 'Include a header row with column names',
    },
    {
      name: 'delimiter',
      type: 'select',
      label: 'Delimiter',
      defaultValue: ',',
      options: [',', ';'],
      description: 'Column separator character',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const text = String(input ?? '').trim();
    const includeHeader = config.includeHeader !== false;
    const delimiter = config.delimiter === ';' ? ';' : ',';

    let parsed: unknown;
    try {
      const parser = new XMLParser({
        ignoreAttributes: false,
        attributeNamePrefix: '@_',
        textNodeName: '#text',
        allowBooleanAttributes: true,
        parseTagValue: true,
        parseAttributeValue: true,
        trimValues: true,
      });
      parsed = parser.parse(text);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Invalid XML';
      throw ToolError.validation(`Invalid XML: ${msg}`, {
        suggestion: 'Check that all tags are properly closed and attributes are quoted',
      });
    }

    const rows = extractRows(parsed);
    if (rows.length === 0) {
      throw ToolError.validation('No tabular data found in XML', {
        suggestion: 'XML should contain repeating child elements under a root element',
      });
    }

    // Collect all unique keys across rows for consistent columns
    const allKeys = Array.from(
      rows.reduce((set, row) => {
        for (const k of Object.keys(row)) set.add(k);
        return set;
      }, new Set<string>())
    );

    const data: Array<Array<string | number | boolean | null>> = [];
    if (includeHeader) {
      data.push(allKeys);
    }
    for (const row of rows) {
      data.push(
        allKeys.map((k) => {
          const v = row[k];
          if (v === null || v === undefined) return null;
          if (typeof v === 'object') return JSON.stringify(v);
          return v as string | number | boolean;
        })
      );
    }

    return Papa.unparse(data, { delimiter, quotes: true });
  },
};

/** Walk parsed XML to find the first array of repeating elements. */
function extractRows(obj: unknown): Record<string, unknown>[] {
  if (Array.isArray(obj)) {
    if (obj.length > 0 && typeof obj[0] === 'object' && obj[0] !== null) {
      return flattenObjects(obj as Record<string, unknown>[]);
    }
    return [];
  }

  if (obj !== null && typeof obj === 'object') {
    const entries = Object.entries(obj as Record<string, unknown>);
    for (const [, value] of entries) {
      if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
        return flattenObjects(value as Record<string, unknown>[]);
      }
    }
    // Try one level deeper (common pattern: <root><items><item>...</item></items></root>)
    for (const [, value] of entries) {
      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        const nested = extractRows(value);
        if (nested.length > 0) return nested;
      }
    }
    // Single object with no arrays — treat as single row
    if (entries.length > 0) {
      return flattenObjects([obj as Record<string, unknown>]);
    }
  }

  return [];
}

/** Flatten one level of nesting in each object for CSV compatibility. */
function flattenObjects(objects: Record<string, unknown>[]): Record<string, unknown>[] {
  return objects.map((obj) => {
    const flat: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        for (const [nestedKey, nestedValue] of Object.entries(value as Record<string, unknown>)) {
          flat[`${key}.${nestedKey}`] = nestedValue;
        }
      } else {
        flat[key] = value;
      }
    }
    return flat;
  });
}
